// ── WhatsAppInbox ──────────────────────────────────────────────────
//
// Three-pane shell. Desktop: all three visible. Tablet: list + chat,
// contact panel toggleable. Mobile: one pane at a time with back
// navigation.
//
// State:
//   • selectedConversationId — URL search-param (?c=<id>) so the
//     state survives reload + can be deep-linked from notifications.
//   • Contact panel collapse — persisted in localStorage.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { ChatWindow } from '../components/ChatWindow';
import { ConversationList } from '../components/ConversationList';
import { ContactPanel } from '../components/ContactPanel';
import { useConversationDetail } from '../hooks/useConversationDetail';

const CONTACT_PANEL_LS_KEY = 'wa.inbox.contactPanelOpen';

export default function WhatsAppInbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('c');
  const [contactPanelOpen, setContactPanelOpen] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(CONTACT_PANEL_LS_KEY);
      return raw === null ? true : raw === 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(CONTACT_PANEL_LS_KEY, String(contactPanelOpen));
    } catch {
      /* ignore */
    }
  }, [contactPanelOpen]);

  const setSelected = (id: string | null) => {
    if (id) {
      setSearchParams({ c: id }, { replace: false });
    } else {
      setSearchParams({}, { replace: false });
    }
  };

  // Detail fetched here as well so the ContactPanel can read it
  // without coupling to ChatWindow. The two consumers share the same
  // hook instance via the realtime invalidation flow.
  const { detail, refresh } = useConversationDetail(selectedId);

  // Mobile pane state: 'list' | 'chat' | 'contact'.
  const mobilePane: 'list' | 'chat' | 'contact' = useMemo(() => {
    if (!selectedId) return 'list';
    if (contactPanelOpen) return 'contact';
    return 'chat';
  }, [selectedId, contactPanelOpen]);

  return (
    <DashboardLayout title="WhatsApp Inbox">
      <div className="flex h-[calc(100vh-7rem)] w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm sm:h-[calc(100vh-8rem)]">
        {/* LEFT PANE — Conversation list */}
        <aside
          className={`flex w-full flex-shrink-0 border-r border-gray-100 md:w-[320px] ${
            // Mobile: show only when in list view
            mobilePane === 'list' ? 'flex' : 'hidden md:flex'
          }`}
        >
          <ConversationList selectedId={selectedId} onSelect={(id) => setSelected(id)} />
        </aside>

        {/* CENTER PANE — Chat window */}
        <main
          className={`flex-1 min-w-0 ${
            mobilePane === 'chat' ? 'flex' : 'hidden md:flex'
          } flex-col`}
        >
          <ChatWindow
            conversationId={selectedId}
            onBack={() => setSelected(null)}
            onToggleContactPanel={() => setContactPanelOpen((v) => !v)}
            contactPanelOpen={contactPanelOpen}
          />
        </main>

        {/* RIGHT PANE — Contact panel (collapsible on desktop, full-screen on mobile) */}
        {contactPanelOpen && (
          <aside
            className={`flex-shrink-0 border-l border-gray-100 ${
              mobilePane === 'contact' ? 'flex w-full' : 'hidden'
            } lg:flex lg:w-[320px]`}
          >
            {selectedId && detail ? (
              <ContactPanel conversation={detail} onChanged={refresh} />
            ) : (
              <div className="flex h-full w-full items-center justify-center px-6 text-center text-sm text-slate-400">
                Select a conversation to see contact details
              </div>
            )}
          </aside>
        )}
      </div>
    </DashboardLayout>
  );
}
